import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Lock, ArrowRight, ArrowLeft, Shield, CheckCircle, Clock, Send, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const ForgotPassword = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [phone, setPhone] = useState('');
    const [otp, setOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [resetToken, setResetToken] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const sendResetCode = async () => {
        if (!phone || phone.length !== 11 || !phone.startsWith('09')) {
            toast.error('شماره موبایل معتبر نیست');
            return;
        }

        setIsLoading(true);
        try {
            const response = await axios.post('/api/auth/forgot-password', { phone });
            if (response.data.success) {
                setResetToken(response.data.resetToken);
                setStep(2);
                setCountdown(120);
                toast.success('کد تأیید ارسال شد');
                const timer = setInterval(() => {
                    setCountdown(prev => {
                        if (prev <= 1) {
                            clearInterval(timer);
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'خطا در ارسال کد تأیید');
        } finally {
            setIsLoading(false);
        }
    };

    const verifyAndReset = async () => {
        if (step === 2) {
            if (!otp || otp.length !== 4) {
                toast.error('کد تأیید 4 رقمی را وارد کنید');
                return;
            }
            setStep(3);
            return;
        }

        if (step === 3) {
            if (!newPassword || newPassword.length < 6) {
                toast.error('رمز عبور باید حداقل 6 کاراکتر باشد');
                return;
            }
            if (newPassword !== confirmPassword) {
                toast.error('رمز عبور و تکرار آن یکسان نیستند');
                return;
            }

            setIsLoading(true);
            try {
                const response = await axios.post('/api/auth/reset-password', {
                    phone,
                    otp,
                    newPassword
                });
                if (response.data.success) {
                    toast.success('رمز عبور با موفقیت تغییر یافت');
                    setTimeout(() => navigate('/login'), 2000);
                } else {
                    toast.error(response.data.message);
                }
            } catch (error) {
                toast.error(error.response?.data?.message || 'خطا در تغییر رمز عبور');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const formatCountdown = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8">
            <div className="max-w-md mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white/80 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden border border-white/20 dark:border-gray-700/30"
                >
                    {/* Header */}
                    <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 text-white p-6 text-center">
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
                        >
                            <img src="/logo.jpg" alt="AvalonMC Logo" className="w-12 h-12 object-cover rounded-xl" />
                        </motion.div>
                        <h1 className="text-2xl font-bold mb-1">بازیابی رمز عبور</h1>
                        <p className="text-blue-100 text-sm">
                            {step === 1 && 'شماره موبایل خود را وارد کنید'}
                            {step === 2 && 'کد تأیید ارسال شده را وارد کنید'}
                            {step === 3 && 'رمز عبور جدید را انتخاب کنید'}
                        </p>
                    </div>

                    {/* فرم */}
                    <div className="p-6">
                        {step === 1 && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        شماره موبایل
                                    </label>
                                    <div className="relative">
                                        <Phone className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                        <input
                                            type="tel"
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            className="w-full pr-10 py-2.5 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm border border-gray-200 dark:border-gray-600 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-all text-gray-900 dark:text-white text-sm"
                                            placeholder="09xxxxxxxxx"
                                            maxLength="11"
                                        />
                                    </div>
                                </div>

                                <button
                                    onClick={sendResetCode}
                                    disabled={isLoading}
                                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-2.5 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2"
                                >
                                    {isLoading ? (
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                        <>
                                            <Send className="w-4 h-4" />
                                            ارسال کد تأیید
                                        </>
                                    )}
                                </button>

                                <Link to="/login" className="flex items-center justify-center gap-1 text-gray-500 dark:text-gray-400 hover:text-blue-600 text-sm transition-colors">
                                    <ArrowLeft className="w-3 h-3" />
                                    بازگشت به صفحه ورود
                                </Link>
                            </div>
                        )}

                        {step === 2 && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        کد تأیید
                                    </label>
                                    <div className="relative">
                                        <Shield className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                        <input
                                            type="text"
                                            value={otp}
                                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                            className="w-full pr-10 py-2.5 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm border border-gray-200 dark:border-gray-600 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-all text-center text-xl font-mono text-gray-900 dark:text-white"
                                            placeholder="0000"
                                            maxLength="4"
                                        />
                                    </div>
                                    {countdown > 0 && (
                                        <p className="text-center text-gray-500 text-xs mt-2 flex items-center justify-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            ارسال مجدد در {formatCountdown(countdown)}
                                        </p>
                                    )}
                                </div>

                                <button
                                    onClick={verifyAndReset}
                                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-2.5 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2"
                                >
                                    <CheckCircle className="w-4 h-4" />
                                    تأیید و ادامه
                                </button>

                                <button
                                    onClick={() => setStep(1)}
                                    className="w-full text-gray-500 dark:text-gray-400 hover:text-blue-600 text-sm py-1"
                                >
                                    شماره موبایل اشتباه است؟
                                </button>
                            </div>
                        )}

                        {step === 3 && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        رمز عبور جدید
                                    </label>
                                    <div className="relative">
                                        <Lock className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            className="w-full pr-10 pl-10 py-2.5 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm border border-gray-200 dark:border-gray-600 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-all text-gray-900 dark:text-white text-sm"
                                            placeholder="رمز عبور جدید را وارد کنید"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        تکرار رمز عبور جدید
                                    </label>
                                    <div className="relative">
                                        <Lock className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                        <input
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            className="w-full pr-10 pl-10 py-2.5 bg-white/50 dark:bg-gray-700/50 backdrop-blur-sm border border-gray-200 dark:border-gray-600 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-all text-gray-900 dark:text-white text-sm"
                                            placeholder="رمز عبور جدید را تکرار کنید"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>

                                <button
                                    onClick={verifyAndReset}
                                    disabled={isLoading}
                                    className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-2.5 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2"
                                >
                                    {isLoading ? (
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                        <>
                                            <CheckCircle className="w-4 h-4" />
                                            تغییر رمز عبور
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default ForgotPassword;